import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import { logger } from '@core/utils';

import { useUpdateMeMutation } from '../api/authApi';
import { selectIsAuthenticated, updateCurrentUser } from '../store/authSlice';

/**
 * Switch the interface language.
 *
 * Before sign-in the choice lives in the browser only (i18next caches it in
 * localStorage). Once signed in it is saved on the user, because the chat
 * agent and the report pipeline read it server-side; the interface switches
 * only after the server accepted it, so the UI never shows a language the
 * agent does not know about.
 */
export function useChangeLanguage() {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [updateMe, { isLoading }] = useUpdateMeMutation();

  const changeLanguage = useCallback(async (language) => {
    if (!isAuthenticated) {
      await i18n.changeLanguage(language);
      return;
    }
    try {
      const user = await updateMe({ language }).unwrap();
      dispatch(updateCurrentUser(user));
    } catch (error) {
      logger.error('Could not save the language preference', error);
      throw error;
    }
  }, [dispatch, i18n, isAuthenticated, updateMe]);

  return { changeLanguage, language: i18n.resolvedLanguage, isSaving: isLoading };
}
